import type { Transport } from "@empite/api-client";

/**
 * The Marketplace API's surface, as typed functions.
 *
 * Hand-written for the same reason apps/admin-portal/src/admin-client.ts is:
 * scripts/contractgen emits @empite/api-client from contracts/openapi, and
 * that document describes the customer and inference surfaces only. The
 * marketplace has no contract file yet, so this module is its hand-maintained
 * counterpart — same RequestSpec shape, same transport, same errors, so it can
 * be deleted wholesale the day a contract lands.
 *
 * Everything here targets /marketplace/v1. Note what no request body in this
 * file has: an `author_user_id` or a `source_application_id`. The server
 * derives both from the session (docs/architecture.md §18), and a client that
 * offered to supply them would be describing a request the API refuses to
 * express.
 */

/** The authenticated mount. Every write, and the reads a member makes. */
const MARKET = "/marketplace/v1";

/** The signed-out mount. Reads only, and only of published, visible work. */
const PUBLIC = "/marketplace/v1/public";

/**
 * Which mount a READ goes to.
 *
 * The two mounts share their handlers on the server, and the difference is
 * the viewer: on `public` there is nobody, so a draft is a 404 even to the
 * person who wrote it. A signed-in visitor must therefore use `member` or
 * they could never open their own unpublished creation — see
 * internal/marketplace/http.go:Handler.Routes.
 */
export type ReadMount = "public" | "member";

function readBase(mount: ReadMount): string {
  return mount === "member" ? MARKET : PUBLIC;
}

// --------------------------------------------------------------------------
// The shapes, mirroring internal/marketplace's JSON tags
// --------------------------------------------------------------------------

/** The author's lever. Publishing is a request, not a guarantee of visibility. */
export type CreationStatus = "draft" | "published" | "unpublished";

/** The moderator's lever, and a separate column on purpose: one column would
 *  let an author unhide themselves by republishing. */
export type ModerationState = "visible" | "hidden" | "removed";

export type FileState =
  | "pending"
  | "uploaded"
  | "scanning"
  | "active"
  | "rejected"
  | "quarantined";

export interface CreationFile {
  id: string;
  creation_id: string;
  position: number;
  /** Display metadata, never dispatched on. The stored object's key is
   *  server-generated and is not in this payload at all. */
  declared_filename: string;
  declared_content_type: string;
  declared_size_bytes: number;
  declared_checksum: string;
  /** What the store MEASURED. Absent until the upload was completed and
   *  verified, which is what makes it the honest figure to render. */
  byte_size?: number;
  checksum_sha256?: string;
  state: FileState;
  required: boolean;
  scan_result?: string;
  uploaded_at?: string;
  created_at: string;
}

export interface Creation {
  id: string;
  author_user_id: string;
  author_display_name?: string;
  source_application_id?: string;
  slug: string;
  title: string;
  summary: string;
  description: string;
  status: CreationStatus;
  moderation_state: ModerationState;
  published_at?: string;
  created_at: string;
  updated_at: string;
  like_count: number;
  comment_count: number;
  download_count: number;
  /** Present on a detail read, absent from list rows. */
  files?: CreationFile[];
}

export interface Counters {
  like_count: number;
  comment_count: number;
  download_count: number;
  /** Always false on the public mount: there is nobody there to have liked it. */
  liked: boolean;
}

export type CommentStatus = "visible" | "hidden" | "removed";

export interface Comment {
  id: string;
  creation_id: string;
  parent_comment_id?: string;
  depth: number;
  author_user_id: string;
  author_display_name?: string;
  body: string;
  status: CommentStatus;
  edited_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Report {
  id: string;
  subject_type: string;
  creation_id?: string;
  comment_id?: string;
  reporter_user_id: string;
  reason_code: string;
  note?: string;
  status: string;
  created_at: string;
}

/** A signed, single-purpose grant. It carries the URL and nothing that would
 *  let a client mint a second one. */
export interface Grant {
  url: string;
  method: string;
  expires_at: string;
  max_bytes: number;
}

export interface UploadTicket {
  file: CreationFile;
  upload: Grant;
}

export interface DownloadTicket {
  url: string;
  method: string;
  expires_at: string;
  /** Server-generated and safe. The grant handler sends it as
   *  Content-Disposition: attachment, so the browser never renders the bytes. */
  filename: string;
  size_bytes: number;
  checksum_sha256: string;
}

export interface CreateCreationBody {
  title: string;
  summary: string;
  description: string;
  /** Optional. The server derives one from the title when it is blank. */
  slug: string;
}

/** A PATCH: an absent field is untouched, a present empty one is cleared. */
export interface PatchCreationBody {
  title?: string;
  summary?: string;
  description?: string;
}

export interface UploadRequestBody {
  filename: string;
  content_type: string;
  size_bytes: number;
  /** Hex SHA-256, computed in the browser. It is a DECLARATION: completion
   *  compares it against what the store measured and discards a mismatch. */
  checksum_sha256: string;
  required?: boolean;
}

export interface CommentBody {
  body: string;
  parent_comment_id?: string;
}

export interface ReportBody {
  reason_code: string;
  note: string;
}

export interface BrowseParams {
  q?: string;
  limit?: number;
  offset?: number;
}

// --------------------------------------------------------------------------
// The client
// --------------------------------------------------------------------------

export interface MarketplaceClient {
  browse(mount: ReadMount, params: BrowseParams): Promise<Creation[]>;
  getCreation(mount: ReadMount, creationID: string): Promise<Creation>;
  getBySlug(mount: ReadMount, slug: string): Promise<Creation>;
  listMine(params: BrowseParams): Promise<Creation[]>;
  createCreation(body: CreateCreationBody): Promise<Creation>;
  updateCreation(creationID: string, body: PatchCreationBody): Promise<Creation>;
  deleteCreation(creationID: string): Promise<void>;
  publish(creationID: string): Promise<Creation>;
  unpublish(creationID: string): Promise<Creation>;
  requestUpload(creationID: string, body: UploadRequestBody): Promise<UploadTicket>;
  completeUpload(creationID: string, fileID: string): Promise<CreationFile>;
  authorizeDownload(creationID: string, fileID: string): Promise<DownloadTicket>;
  counters(mount: ReadMount, creationID: string): Promise<Counters>;
  setLike(creationID: string, liked: boolean): Promise<Counters>;
  listComments(mount: ReadMount, creationID: string, params: BrowseParams): Promise<Comment[]>;
  addComment(creationID: string, body: CommentBody): Promise<Comment>;
  editComment(commentID: string, body: string): Promise<Comment>;
  deleteComment(commentID: string): Promise<void>;
  reportCreation(creationID: string, body: ReportBody): Promise<Report>;
  reportComment(commentID: string, body: ReportBody): Promise<Report>;
  /** The author's own hide/restore/remove over a comment under their work.
   *  The same verbs an operator uses, recorded against a different column. */
  moderateComment(commentID: string, action: "hide" | "restore" | "remove", body: ReportBody): Promise<Comment>;
  reportReasons(mount: ReadMount): Promise<string[]>;
}

export function createMarketplaceClient(transport: Transport): MarketplaceClient {
  return {
    browse: (mount, params) =>
      transport
        .request<{ creations: Creation[] | null }>({
          operationId: "marketplace.browse",
          method: "GET",
          path: `${readBase(mount)}/creations`,
          query: { q: params.q, limit: params.limit, offset: params.offset },
        })
        // A no-result search is `null` on the wire, not `[]`. Normalising it
        // here rather than in every screen is what stops one of them mapping
        // over null.
        .then((r) => r.creations ?? []),

    getCreation: (mount, creationID) =>
      transport
        .request<{ creation: Creation }>({
          operationId: "marketplace.getCreation",
          method: "GET",
          path: `${readBase(mount)}/creations/${encodeURIComponent(creationID)}`,
        })
        .then((r) => r.creation),

    getBySlug: (mount, slug) =>
      transport
        .request<{ creation: Creation }>({
          operationId: "marketplace.getBySlug",
          method: "GET",
          path: `${readBase(mount)}/by-slug/${encodeURIComponent(slug)}`,
        })
        .then((r) => r.creation),

    listMine: (params) =>
      transport
        .request<{ creations: Creation[] | null }>({
          operationId: "marketplace.listMine",
          method: "GET",
          path: `${MARKET}/me/creations`,
          query: { limit: params.limit, offset: params.offset },
        })
        .then((r) => r.creations ?? []),

    createCreation: (body) =>
      transport
        .request<{ creation: Creation }>({
          operationId: "marketplace.createCreation",
          method: "POST",
          path: `${MARKET}/creations`,
          body,
        })
        .then((r) => r.creation),

    updateCreation: (creationID, body) =>
      transport
        .request<{ creation: Creation }>({
          operationId: "marketplace.updateCreation",
          method: "PATCH",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}`,
          body,
        })
        .then((r) => r.creation),

    deleteCreation: (creationID) =>
      transport.request<void>({
        operationId: "marketplace.deleteCreation",
        method: "DELETE",
        path: `${MARKET}/creations/${encodeURIComponent(creationID)}`,
      }),

    publish: (creationID) =>
      transport
        .request<{ creation: Creation }>({
          operationId: "marketplace.publish",
          method: "POST",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/publish`,
        })
        .then((r) => r.creation),

    unpublish: (creationID) =>
      transport
        .request<{ creation: Creation }>({
          operationId: "marketplace.unpublish",
          method: "POST",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/unpublish`,
        })
        .then((r) => r.creation),

    requestUpload: (creationID, body) =>
      transport.request<UploadTicket>({
        operationId: "marketplace.requestUpload",
        method: "POST",
        path: `${MARKET}/creations/${encodeURIComponent(creationID)}/files`,
        body,
      }),

    // No body: completion compares what the STORE measured against what was
    // declared when the grant was issued. A size or checksum accepted here
    // would be the client marking its own homework.
    completeUpload: (creationID, fileID) =>
      transport
        .request<{ file: CreationFile }>({
          operationId: "marketplace.completeUpload",
          method: "POST",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/files/${encodeURIComponent(fileID)}/complete`,
        })
        .then((r) => r.file),

    authorizeDownload: (creationID, fileID) =>
      transport
        .request<{ download: DownloadTicket }>({
          operationId: "marketplace.authorizeDownload",
          method: "GET",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/files/${encodeURIComponent(fileID)}/download`,
        })
        .then((r) => r.download),

    counters: (mount, creationID) =>
      transport
        .request<{ counters: Counters }>({
          operationId: "marketplace.counters",
          method: "GET",
          path: `${readBase(mount)}/creations/${encodeURIComponent(creationID)}/counters`,
        })
        .then((r) => r.counters),

    // PUT and DELETE rather than a toggle: the state the caller wants is in
    // the method, so a retry of either lands where the first one did.
    setLike: (creationID, liked) =>
      transport
        .request<{ counters: Counters }>({
          operationId: liked ? "marketplace.like" : "marketplace.unlike",
          method: liked ? "PUT" : "DELETE",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/like`,
        })
        .then((r) => r.counters),

    listComments: (mount, creationID, params) =>
      transport
        .request<{ comments: Comment[] | null }>({
          operationId: "marketplace.listComments",
          method: "GET",
          path: `${readBase(mount)}/creations/${encodeURIComponent(creationID)}/comments`,
          query: { limit: params.limit, offset: params.offset },
        })
        .then((r) => r.comments ?? []),

    addComment: (creationID, body) =>
      transport
        .request<{ comment: Comment }>({
          operationId: "marketplace.addComment",
          method: "POST",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/comments`,
          body,
        })
        .then((r) => r.comment),

    editComment: (commentID, body) =>
      transport
        .request<{ comment: Comment }>({
          operationId: "marketplace.editComment",
          method: "PATCH",
          path: `${MARKET}/comments/${encodeURIComponent(commentID)}`,
          body: { body },
        })
        .then((r) => r.comment),

    deleteComment: (commentID) =>
      transport.request<void>({
        operationId: "marketplace.deleteComment",
        method: "DELETE",
        path: `${MARKET}/comments/${encodeURIComponent(commentID)}`,
      }),

    reportCreation: (creationID, body) =>
      transport
        .request<{ report: Report }>({
          operationId: "marketplace.reportCreation",
          method: "POST",
          path: `${MARKET}/creations/${encodeURIComponent(creationID)}/report`,
          body,
        })
        .then((r) => r.report),

    reportComment: (commentID, body) =>
      transport
        .request<{ report: Report }>({
          operationId: "marketplace.reportComment",
          method: "POST",
          path: `${MARKET}/comments/${encodeURIComponent(commentID)}/report`,
          body,
        })
        .then((r) => r.report),

    moderateComment: (commentID, action, body) =>
      transport
        .request<{ comment: Comment }>({
          operationId: `marketplace.moderateComment.${action}`,
          method: "POST",
          path: `${MARKET}/comments/${encodeURIComponent(commentID)}/${action}`,
          body,
        })
        .then((r) => r.comment),

    // Fetched, never hard-coded: a client carrying its own copy of the closed
    // set is a second list to keep in step with the server's.
    reportReasons: (mount) =>
      transport
        .request<{ reason_codes: string[] | null }>({
          operationId: "marketplace.reportReasons",
          method: "GET",
          path: `${readBase(mount)}/report-reasons`,
        })
        .then((r) => r.reason_codes ?? []),
  };
}
